import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Loader2, KeyRound, Pencil, Copy, Check, ShieldAlert, Users, GraduationCap, Search } from "lucide-react";
import { toast } from "sonner";
import DevAdminStudentPasswords from "@/pages/DevAdminStudentPasswords";

const relTime = (iso) => {
  if (!iso) return "Never signed in";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
};

export default function DevAdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("admin");

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="devadmin-users-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-fuchsia-600 text-white flex items-center justify-center">
            <UserCog className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Manage Users</h1>
            <p className="text-sm text-slate-500">Edit staff details, reset passwords and manage student logins.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-100 dark:bg-slate-800/60" data-testid="manage-users-tabs">
            <TabsTrigger value="admin" data-testid="manage-users-tab-admin" className="gap-1.5"><ShieldAlert className="h-4 w-4" /> Admin</TabsTrigger>
            <TabsTrigger value="teacher" data-testid="manage-users-tab-teacher" className="gap-1.5"><Users className="h-4 w-4" /> Teacher</TabsTrigger>
            <TabsTrigger value="students" data-testid="manage-users-tab-students" className="gap-1.5"><GraduationCap className="h-4 w-4" /> Students</TabsTrigger>
          </TabsList>

          <TabsContent value="admin" className="mt-6"><StaffManager role="admins" /></TabsContent>
          <TabsContent value="teacher" className="mt-6"><StaffManager role="teacher" /></TabsContent>
          <TabsContent value="students" className="mt-6"><DevAdminStudentPasswords embedded /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function StaffManager({ role }) {
  const isTeacher = role === "teacher";
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [tempResult, setTempResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devadmin/privileged", { params: isTeacher ? { role: "teacher" } : {} });
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load accounts");
    } finally { setLoading(false); }
  }, [isTeacher]);

  useEffect(() => { load(); }, [load]);

  const doReset = async () => {
    const acc = resetting;
    setResetting(null);
    try {
      const { data } = await api.post(`/devadmin/privileged/${acc.id}/reset-password`);
      setTempResult({ name: acc.name, temp_password: data.temp_password });
      setCopied(false);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not reset password");
    }
  };

  const visible = accounts.filter((a) => {
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (a.name || "").toLowerCase().includes(t) || (a.phone || "").includes(t) || (a.email || "").toLowerCase().includes(t);
  });

  return (
    <div>
      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / email" className="h-10 rounded-xl pl-9" data-testid={`staff-search-${role}`} />
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
      ) : (
        <div className="space-y-3" data-testid={`staff-list-${role}`}>
          {visible.map((a) => (
            <div key={a.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex items-center justify-between gap-4" data-testid={`staff-account-${a.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">{a.name}</span>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full ${a.role === "super_admin" ? "bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300" : a.role === "teacher" ? "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300" : "bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300"}`}>
                    {a.role === "super_admin" ? "Super Admin" : a.role === "teacher" ? "Teacher" : "Admin"}
                  </span>
                  {a.must_change_password && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" data-testid={`staff-mustchange-${a.id}`}>Pending change</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{a.phone || "No phone"}{a.email ? ` · ${a.email}` : ""}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">Last login: {relTime(a.last_login_at)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setEditing(a)} data-testid={`edit-staff-${a.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" className="rounded-xl bg-rose-500 hover:bg-rose-600" onClick={() => setResetting(a)} data-testid={`reset-staff-${a.id}`}>
                  <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                </Button>
              </div>
            </div>
          ))}
          {visible.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No {isTeacher ? "teacher" : "admin"} accounts found.</div>}
        </div>
      )}

      <EditDialog account={editing} onClose={() => setEditing(null)} onSaved={load} />

      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}><ShieldAlert className="inline h-5 w-5 mr-2 -mt-1 text-rose-500" />Reset password?</DialogTitle>
            <DialogDescription>
              This sets a new temporary password for <strong>{resetting?.name}</strong> and forces them to choose a new one on next login. You'll get the temporary password to share.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600" onClick={doReset} data-testid="reset-confirm-btn">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempResult} onOpenChange={(v) => !v && setTempResult(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}><KeyRound className="inline h-5 w-5 mr-2 -mt-1 text-sky-500" />Temporary password</DialogTitle>
            <DialogDescription>Share this with {tempResult?.name}. They must change it on next login. It won't be shown again.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-3">
            <code className="flex-1 text-lg font-mono text-slate-900 dark:text-slate-100" data-testid="temp-password-value">{tempResult?.temp_password}</code>
            <Button size="sm" variant="outline" className="rounded-lg" onClick={() => {
              navigator.clipboard?.writeText(tempResult?.temp_password || "");
              setCopied(true); toast.success("Copied");
            }} data-testid="copy-temp-password">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempResult(null)} data-testid="temp-password-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditDialog({ account, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (account) {
      setName(account.name || "");
      setEmail(account.email || "");
      setPhone((account.phone || "").replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "").slice(-10));
    }
  }, [account]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/devadmin/privileged/${account.id}`, { name, email, phone });
      toast.success("Account updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update");
    } finally { setSaving(false); }
  };

  const roleLabel = account?.role === "super_admin" ? "Super Admin" : account?.role === "teacher" ? "Teacher" : "Admin";

  return (
    <Dialog open={!!account} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="edit-priv-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Edit {roleLabel}</DialogTitle>
          <DialogDescription>Update the display name, email and login mobile number.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="e-name">Name</Label>
            <Input id="e-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" data-testid="edit-priv-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="e-email">Email</Label>
            <Input id="e-email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" data-testid="edit-priv-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="e-phone">Mobile number (login)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">+91</span>
              <Input id="e-phone" inputMode="numeric" maxLength={10} value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="h-11 rounded-xl pl-12" data-testid="edit-priv-phone" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-sky-500 hover:bg-sky-600" onClick={save} disabled={saving} data-testid="save-priv-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
